/**
 * Where a work order is, which trade does it, and whose contract it falls in.
 *
 * Airport work is not only the airfield. The terminal's civil, electrical and
 * electro-mechanical repairs and the landside's parking, entrances, landscape,
 * cleaning and environmental services run through the same register, and most
 * of it is in a contractor's scope. The platform keeps the three in the
 * order's attrs (`area`, `trade`, `contractor`, checked in
 * `backend/api/slt/work/workorders.py`); the keys here must match it.
 *
 * An order raised before these existed has none of them, so each is read
 * from what the order already carries — the asset's or zone's discipline, a
 * facility inspection's "Trade:" line, the facility's own domain — and marked
 * as worked out rather than set. Nothing is guessed from the title.
 */
import type { WorkOrder } from "./workOrders";
import { FACILITY_BY_ID } from "./facilityRegister";
import { facilityOfOrder, tradeOfOrder } from "./assetInspections";

export const AREAS = ["airside", "terminal", "landside"] as const;
export type Area = (typeof AREAS)[number];

export const AREA_LABEL: Record<Area, string> = {
  airside: "Airside",
  terminal: "Terminal",
  landside: "Landside",
};

/** What each area covers, for the picker's hint. */
export const AREA_HINT: Record<Area, string> = {
  airside: "Runways, taxiways, aprons, lights, navaids",
  terminal: "Buildings, halls, gates, baggage, lifts",
  landside: "Parking, roads, entrances, landscape, utilities",
};

/** The first seven are Facility inspections' trades, so its orders read the same. */
export const TRADES = [
  "civil",
  "electrical",
  "electromech",
  "fire",
  "utilities",
  "cleaning",
  "pest",
  "landscape",
  "environmental",
  "airfield_systems",
  "it_systems",
  "other",
] as const;
export type Trade = (typeof TRADES)[number];

export const TRADE_LABEL: Record<Trade, string> = {
  civil: "Civil",
  electrical: "Electrical",
  electromech: "Electro-mechanical",
  fire: "Fire systems",
  utilities: "Utilities",
  cleaning: "Cleaning",
  pest: "Pest control",
  landscape: "Landscape",
  environmental: "Environmental",
  airfield_systems: "Airfield lighting and navaids",
  it_systems: "IT and systems",
  other: "Other",
};

export const TRADE_HINT: Record<Trade, string> = {
  civil: "Pavement, roads, buildings, markings",
  electrical: "Power, lighting, substations",
  electromech: "HVAC, lifts, escalators, baggage, doors",
  fire: "Alarms, sprinklers, suppression",
  utilities: "Water, drainage, sewage, cooling",
  cleaning: "Cleaning services",
  pest: "Pest control",
  landscape: "Irrigation, planting, grounds",
  environmental: "Waste, noise, air, spills",
  airfield_systems: "AGL, navaids, signs, MET",
  it_systems: "IT, FIDS, CCTV, access control",
  other: "Anything else",
};

/** The platform's discipline (asset or zone domain) → area. Absent = not known. */
const AREA_OF_DOMAIN: Record<string, Area> = {
  airfield: "airside",
  airside: "airside",
  terminal: "terminal",
  passenger_processing: "terminal",
  conveyance: "terminal",
  baggage: "terminal",
  boarding: "terminal",
  landside: "landside",
  environment: "landside",
  estate: "landside",
};

/** Discipline → trade, only where the discipline says it outright. */
const TRADE_OF_DOMAIN: Record<string, Trade> = {
  utilities: "utilities",
  environment: "environmental",
  conveyance: "electromech",
  baggage: "electromech",
  boarding: "electromech",
  civil: "civil",
  mechanical: "electromech",
};

export interface Classified<T> {
  value: T | null;
  /** true when read from what the order carries, not set on it */
  derived: boolean;
}

const attr = (wo: WorkOrder, key: string): string | undefined => {
  const v = wo.attrs?.[key];
  return typeof v === "string" && v.trim() ? v.trim() : undefined;
};

export function areaOf(wo: WorkOrder): Classified<Area> {
  const set = attr(wo, "area");
  if (set && (AREAS as readonly string[]).includes(set)) return { value: set as Area, derived: false };
  const facility = facilityOfOrder(wo);
  const domain = (facility && FACILITY_BY_ID.get(facility)?.domain) || wo.discipline;
  return { value: (domain && AREA_OF_DOMAIN[domain]) || null, derived: true };
}

export function tradeOf(wo: WorkOrder): Classified<Trade> {
  const set = attr(wo, "trade");
  if (set && (TRADES as readonly string[]).includes(set)) return { value: set as Trade, derived: false };
  const fromRound = tradeOfOrder(wo);
  if (fromRound) return { value: fromRound, derived: true };
  return { value: (wo.discipline && TRADE_OF_DOMAIN[wo.discipline]) || null, derived: true };
}

/** The contractor's name, or null for the airport's own team / not recorded. */
export function contractorOf(wo: WorkOrder): string | null {
  return attr(wo, "contractor") ?? null;
}

/** What the list filters on: a key, or "none" for not set. */
export const NOT_SET = "none";
export const OWN_TEAM = "Own team";

export function areaKey(wo: WorkOrder): string {
  return areaOf(wo).value ?? NOT_SET;
}
export function tradeKey(wo: WorkOrder): string {
  return tradeOf(wo).value ?? NOT_SET;
}
/** The contractor, or "Own team" when the order names none. */
export function contractorKey(wo: WorkOrder): string {
  return contractorOf(wo) ?? OWN_TEAM;
}

/** Contractor names to offer: the register's providers, then any already on orders. */
export function contractorChoices(providers: readonly string[], orders: readonly WorkOrder[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const n of [...providers, ...orders.map(contractorOf)]) {
    const name = n?.trim();
    if (name && !seen.has(name.toLowerCase())) {
      seen.add(name.toLowerCase());
      out.push(name);
    }
  }
  return out.sort((a, b) => a.localeCompare(b));
}
